import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import type {
  OptionListItem,
  PerformanceTimeframe,
  SortDirection,
} from '@/types/optionDiscovery';
import {
  generateAllOptions,
  calculateOptionPerformance,
  getAvailableTimeframes,
  fetchExtendedPriceHistory,
} from '@services/optionDiscoveryService';
import type { SecondPricePoint } from '@services/priceService';
import { RoundService } from '@services/roundService';
import type { Asset } from '@shared/types';

import { usePrices } from './usePrices';

export interface UseOptionsListOptions {
  timeframe?: PerformanceTimeframe;
  sortDirection?: SortDirection;
  assetFilter?: Asset | 'ALL';
}

export interface UseOptionsListResult {
  options: OptionListItem[];
  isLoading: boolean;
  error: string | null;
  availableTimeframes: PerformanceTimeframe[];
  refresh: () => void;
}

const REFETCH_INTERVAL_MS = 60 * 1000; // Refetch candles every 1 minute to get new data points
const ALL_ASSETS: Asset[] = ['BTC', 'ETH', 'SOL' /* , 'XRP' */];

export function useOptionsList(
  opts: UseOptionsListOptions = {},
): UseOptionsListResult {
  const {
    timeframe: _timeframe = 'all',
    sortDirection = 'desc',
    assetFilter = 'ALL',
  } = opts;

  // Get live price data for real-time updates
  const pricesQuery = usePrices();
  const livePrices = pricesQuery.data;

  const [historyMap, setHistoryMap] = useState<Map<Asset, SecondPricePoint[]>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lastFetchTime = useRef<number>(0);

  // Fetch extended history for all assets
  const fetchAllHistory = useCallback(async () => {
    const assets = assetFilter === 'ALL' ? ALL_ASSETS : [assetFilter];

    const newHistoryMap = new Map<Asset, SecondPricePoint[]>();

    await Promise.all(
      assets.map(async asset => {
        try {
          const history = await fetchExtendedPriceHistory(asset);
          newHistoryMap.set(asset, history);
        } catch (err) {
          console.error(`Failed to fetch history for ${asset}:`, err);
          newHistoryMap.set(asset, []);
        }
      }),
    );

    setHistoryMap(newHistoryMap);
    lastFetchTime.current = Date.now();
    setIsLoading(false);
  }, [assetFilter]);

  const refresh = useCallback(() => {
    setHistoryMap(new Map());
    lastFetchTime.current = 0;
    setIsLoading(true);
    fetchAllHistory();
  }, [fetchAllHistory]);

  // Initial fetch and periodic refetch for new candles
  useEffect(() => {
    // Wait for price service to bootstrap
    if (pricesQuery.isLoading) {
      return;
    }

    // Initial fetch
    fetchAllHistory();

    // Set up interval to refetch candles periodically
    const intervalId = setInterval(() => {
      fetchAllHistory();
    }, REFETCH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [pricesQuery.isLoading, fetchAllHistory]);

  // Calculate options reactively when historyMap or livePrices change
  const { options, availableTimeframes } = useMemo(() => {
    if (historyMap.size === 0) {
      return {
        options: [],
        availableTimeframes: ['all'] as PerformanceTimeframe[],
      };
    }

    try {
      // Get all option identifiers
      const allOptionIds = generateAllOptions();

      // Filter by asset if specified
      const filteredOptionIds =
        assetFilter === 'ALL'
          ? allOptionIds
          : allOptionIds.filter(opt => opt.asset === assetFilter);

      // Check if round is expired
      const roundInfo = RoundService.getCurrentRoundInfo();
      if (roundInfo.isExpired) {
        return {
          options: [],
          availableTimeframes: ['all'] as PerformanceTimeframe[],
        };
      }

      // Calculate available timeframes from the first asset's history
      const firstAsset = assetFilter === 'ALL' ? 'BTC' : assetFilter;
      const firstAssetHistory = historyMap.get(firstAsset) ?? [];
      const timeframesAvailable = getAvailableTimeframes(firstAssetHistory);

      // Calculate performance for each option using LIVE prices
      const optionItems: OptionListItem[] = [];

      for (const optionId of filteredOptionIds) {
        const priceHistory = historyMap.get(optionId.asset) ?? [];
        // Pass live spot price for real-time option price calculation
        const liveSpotPrice = livePrices?.get(optionId.asset)?.price;
        const item = calculateOptionPerformance(
          optionId,
          priceHistory,
          liveSpotPrice,
        );

        if (item) {
          optionItems.push(item);
        }
      }

      // Sort by price change percent
      const sortedOptions = [...optionItems].sort((a, b) => {
        const diff = a.priceChangePercent - b.priceChangePercent;
        return sortDirection === 'desc' ? -diff : diff;
      });

      setError(null);
      return {
        options: sortedOptions,
        availableTimeframes: timeframesAvailable,
      };
    } catch (err) {
      console.error('Error calculating options list:', err);
      setError('Failed to load options');
      return {
        options: [],
        availableTimeframes: ['all'] as PerformanceTimeframe[],
      };
    }
  }, [historyMap, livePrices, assetFilter, sortDirection]);

  return useMemo(
    () => ({
      options,
      isLoading,
      error,
      availableTimeframes,
      refresh,
    }),
    [options, isLoading, error, availableTimeframes, refresh],
  );
}
